import { ApolloClient, ApolloLink, InMemoryCache, HttpLink, from } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import config from 'src/config';
import { rateLimitErrorLink } from './rateLimitErrorLink';

/**
 * Error handling link for GraphQL errors
 * Logs GraphQL and network errors for debugging
 */
const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Code: ${extensions?.code}`
      );
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

/**
 * HTTP link configuration for the Property Insurance API
 * Endpoint: /webshop (GraphQL)
 */
const httpLink = new HttpLink({
  uri: [config.REACT_APP_API_URL, 'webshop'].join('/'),
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * WebSocket link for GraphQL subscriptions (graphql-ws protocol)
 * Same /webshop path, upgraded via standard HTTP Upgrade
 */
const wsUrl = config.REACT_APP_API_URL
  .replace(/^https:/, 'wss:')
  .replace(/^http:/, 'ws:') + '/webshop';

const wsLink = new GraphQLWsLink(createClient({
  url: wsUrl,
  lazy: false,
  retryAttempts: 5,
  shouldRetry: () => true,
}));

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  // rateLimitErrorLink toasts RATE_LIMITED / IP_BLOCKED denials; CAPTCHA_REQUIRED is
  // left to the submitting form (useCreateSoWithCaptcha).
  from([rateLimitErrorLink, errorLink, httpLink]),
);

/**
 * Apollo cache configuration with type policies
 */
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        getSo: {
          merge: true,
        },
        getOffer: {
          merge: true,
        },
      },
    },
  },
  addTypename: false
});

/**
 * Apollo Client instance for Property Insurance API
 * 
 * Usage:
 * - Import and use with useQuery/useMutation hooks
 * - Or use apolloClient.query/mutate directly
 */
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    // Mutations FAIL: a GraphQL error rejects the promise, so call sites reach their
    // catch (and toast) instead of advancing on `data: null`. With 'all' a denied or
    // failed mutation resolved silently and the funnel moved on as if it had worked
    // (review finding 2026-09-23). Queries keep 'all' — partial data is rendered.
    mutate: {
      errorPolicy: 'none',
    },
  },
});
